import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Copy, Check } from 'lucide-react'

interface CopyableEmailProps {
  email: string
  displayText?: React.ReactNode
  showCopyIcon?: boolean
  className?: string
  wrapperClassName?: string
  iconSize?: string
  copyLabel?: string
  ariaLabel?: string
  subject?: string
}

const splitEmail = (email: string) => {
  // Don't return the full email; split so it's not in SSR HTML
  const [local, domain] = email.split('@')
  if (!local || !domain) {
    throw new Error('Invalid email format')
  }
  const lastDot = domain.lastIndexOf('.')
  return {
    local,
    host: lastDot > 0 ? domain.slice(0, lastDot) : domain,
    tld: lastDot > 0 ? domain.slice(lastDot + 1) : '',
  }
}

const joinEmail = (parts: { local: string; host: string; tld: string }) =>
  parts.tld ? `${parts.local}@${parts.host}.${parts.tld}` : `${parts.local}@${parts.host}`

const ObfuscatedEmail: React.FC<CopyableEmailProps> = ({
  email,
  displayText,
  showCopyIcon = true,
  className = 'l0-email underline underline-offset-2',
  wrapperClassName,
  iconSize = 'w-4 h-4',
  copyLabel = 'Copy email address',
  ariaLabel = 'Email address',
  subject
}) => {
  const [copied, setCopied] = useState(false)
  const [hydrated, setHydrated] = useState(false)
  const [visibleEmail, setVisibleEmail] = useState<string | React.ReactNode>('') // rendered for humans post-hydration
  const liveRegionRef = useRef<HTMLSpanElement | null>(null)

  const parts = useMemo(() => splitEmail(email), [email])
  const obfuscatedText = useMemo(() => {
    // If displayText is provided, use it for obfuscation, otherwise use email parts
    if (displayText) {
      return displayText
    }
    // Shown pre-hydration for bots viewing raw HTML; readable to humans
    const dotPart = parts.tld ? ` [...] ${parts.tld}` : ''
    return `${parts.local} [at] ${parts.host}${dotPart}`
  }, [parts, displayText])

  useEffect(() => {
    // Hydrate on client: join and render a proper mailto or display text
    if (displayText) {
      setVisibleEmail(displayText)
    } else {
      setVisibleEmail(joinEmail(parts))
    }
    setHydrated(true)
  }, [parts, displayText])

  const copyToClipboard = async () => {
    // Always copy the actual email address, not the display text
    const text = joinEmail(parts)
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
        setCopied(true)
        // Announce to SR users
        if (liveRegionRef.current) {
          liveRegionRef.current.textContent = 'Email copied to clipboard'
        }
        setTimeout(() => {
          setCopied(false)
          if (liveRegionRef.current) liveRegionRef.current.textContent = ''
        }, 2000)
      } else {
        // Fallback: select the email text for manual copying
        const node = document.getElementById('email-text')
        if (node) {
          const range = document.createRange()
          range.selectNodeContents(node)
          const sel = window.getSelection()
          sel?.removeAllRanges()
          sel?.addRange(range)

          // Provide feedback that user needs to copy manually
          if (liveRegionRef.current) {
            liveRegionRef.current.textContent = 'Email selected. Press Ctrl+C (or Cmd+C) to copy'
          }
          setTimeout(() => {
            if (liveRegionRef.current) liveRegionRef.current.textContent = ''
          }, 3000)
        } else {
          throw new Error('Could not select email text')
        }
      }
    } catch (err) {
      console.error('Failed to copy email:', err)
      // Provide user feedback for failed copy
      if (liveRegionRef.current) {
        liveRegionRef.current.textContent = 'Copy failed. Please select and copy the email manually'
      }
      setTimeout(() => {
        if (liveRegionRef.current) liveRegionRef.current.textContent = ''
      }, 3000)
    }
  }

  return (
    <span className={`inline-flex items-center gap-2 ${wrapperClassName || ''}`}>
      {/* Live region for accessibility feedback [3] */}
      <span ref={liveRegionRef} aria-live="polite" className="sr-only"></span>

      {/* Progressive enhancement:
         - Before hydration: show obfuscated text to avoid exposing raw email in HTML
         - After hydration: show a proper mailto link */}
      {hydrated ? (
        <a
          id="email-text"
          className={className}
          href={`mailto:${joinEmail(parts)}${subject ? `?subject=${encodeURIComponent(subject)}` : ''}`}
          aria-label={ariaLabel}
        >
          {visibleEmail}
        </a>
      ) : (
        <span
          id="email-text"
          className={className}
          // Expose only pieces via data-attrs; scrapers that don’t run JS won’t get the full address [7]
          data-local={parts.local}
          data-host={parts.host}
          data-tld={parts.tld}
        >
          {obfuscatedText}
        </span>
      )}

      {showCopyIcon && (
        <button
          type="button"
          onClick={copyToClipboard}
          className="email-copy inline-flex items-center justify-center"
          title={copyLabel}
          aria-label={copyLabel}
        >
          {copied ? <Check className={iconSize} /> : <Copy className={iconSize} />}
        </button>
      )}
    </span>
  )
}

export { ObfuscatedEmail }
export default ObfuscatedEmail